import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";
import { useTranslation } from "react-i18next";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const { i18n } = useTranslation();

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setCompany(null);
      return null;
    }
    try {
      const meRes = await api.get("/auth/me");
      const meUser = meRes.data;
      setUser(meUser);
      // Super admins don't belong to a tenant — skip the /company fetch.
      if (meUser?.is_super_admin) {
        setCompany(null);
      } else {
        try {
          const coRes = await api.get("/company");
          setCompany(coRes.data);
        } catch {
          setCompany(null);
        }
      }
      // Adopt user preferred language on session bootstrap (only if user hasn't picked another).
      const stored = localStorage.getItem("invoicepro.lang");
      if (!stored && meUser?.preferred_language) {
        i18n.changeLanguage(meUser.preferred_language);
      }
      return meUser;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setCompany(null);
      return null;
    }
  }, [i18n]);

  useEffect(() => {
    refresh().finally(() => setBootstrapping(false));
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      if (data.user?.is_super_admin) {
        setCompany(null);
      } else {
        try {
          const co = await api.get("/company");
          setCompany(co.data);
        } catch {
          setCompany(null);
        }
      }
      if (data.user?.preferred_language) {
        // Don't override an explicit language pick from the login screen.
        const explicit = localStorage.getItem("invoicepro.lang");
        if (!explicit) i18n.changeLanguage(data.user.preferred_language);
      }
      return data.user;
    },
    [i18n]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setCompany(null);
  }, []);

  const setSession = useCallback((token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    return refresh();
  }, [refresh]);

  const applySession = useCallback((token, u, co) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    if (co !== undefined) setCompany(co);
  }, []);

  const value = {
    user,
    company,
    bootstrapping,
    isAuthenticated: !!user,
    login,
    logout,
    refresh,
    setCompany,
    setSession,
    applySession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
